import clsx from 'clsx'
import { useState } from 'react'
import { useLocation, useParams } from 'react-router-dom'

import {
  ApiTradingAccount,
  useGetByTenantAccountAndAccountSourceAccountIdentifierQuery,
  useGetByTenantAccountInprogressAndAccountIdentifierQuery
} from '@/api/rtk-api'
import { Link, LinkButton, Rule, Space, Tabs } from '@/components/core-v2'
import ToastNotification from '@/components/core-v2/atoms/toast-notification/toastNotification'
import { ConfirmationModal } from '@/components/core-v2/organisms/confirmation-modal'
import { PATH, TENANT, TODAY } from '@/const'
import { useUser } from '@/utilities/hooks/useUser'

import { getUserDisplayInfo, isCollateralUser } from '../../../tradebuilder-v2/util/user'
import classes from './index.module.scss'
import { useAmendFees } from './useAmendFees'

interface TradeTabListProps {
  internal?: boolean
  tradingAccount?: ApiTradingAccount
}

const TradeTabList = ({ internal, tradingAccount }: TradeTabListProps) => {
  const { source, tradeId } = useParams()
  const { data: user } = useUser()
  const { userRoles } = getUserDisplayInfo(user)
  const { pathname } = useLocation()
  const page = pathname.split('/')[5]

  const { data, isLoading } = useGetByTenantAccountAndAccountSourceAccountIdentifierQuery({
    tenant: TENANT,
    accountIdentifier: tradeId,
    accountSource: source
  })

  const { amendFees, dismissError, error: amendError, isAmending } = useAmendFees()
  const [isConfirmingAmend, setIsConfirmingAmend] = useState(false)

  if (isLoading) return null

  const isEditableWithCollateral =
    tradingAccount?.note &&
    (!tradingAccount.note.isCollateralForLoan ||
      (tradingAccount.note.isCollateralForLoan && isCollateralUser(userRoles)))

  const isEditable =
    data.account.status !== 'Closed' &&
    internal &&
    !data.account.currentlyApproving &&
    !data.account.currentlyClosing &&
    !tradingAccount?.isLockedForUpdates &&
    isEditableWithCollateral &&
    !user.roles.includes('BusinessUnitUser')

  // ABSA-10814: "Amend fees" is only offered for an ACTIVE account on a model (or fund-of-funds)
  // portfolio. Self-select accounts are excluded. The precise lifecycle status lives on the trading
  // account (data.account.status is the coarse running status). The portfolio id is not declared on
  // the swagger type but is present at runtime (see portfolio-slice's readonly mapping), and is
  // required to drive the streamlined amendment - so only show the action when it can be resolved.
  const currentPortfolio = tradingAccount?.currentAccountState?.portfolio
  const currentPortfolioId = (currentPortfolio as { portfolioId?: string })?.portfolioId
  const canAmendFees =
    isEditable &&
    tradingAccount?.status === 'Active' &&
    (currentPortfolio?.type === 'ModelPortfolio' || currentPortfolio?.type === 'FundOfFunds') &&
    !!currentPortfolioId

  const isClosable = tradingAccount?.note.isCollateralForLoan
    ? isCollateralUser(userRoles)
    : (isEditable && user.roles.includes('Administrator')) ||
      user.roles.includes('BankAdmin') ||
      user.roles.includes('Sales') ||
      user.roles.includes('Editor')

  return (
    <>
      <div className={clsx(classes.root, 'pl-4', 'pr-6')}>
        <Tabs.List aria-label="Trade pages" showRule={false} spacing="lg">
          <Tabs.Tab
            isSelected={page === 'performance'}
            to={PATH.reportsSingleTradePerformance(source, tradeId)}
            value="performance"
          >
            Performance
          </Tabs.Tab>
          <Tabs.Tab
            isSelected={page === 'events'}
            to={PATH.reportsSingleTradeEventsHoldings(source, tradeId, TODAY)}
            value="events"
          >
            Events
          </Tabs.Tab>
          <Tabs.Tab
            isSelected={page === 'details'}
            to={PATH.reportsSingleTradeDetailsNote(source, tradeId)}
            value="details"
          >
            Details
          </Tabs.Tab>
          <Tabs.Tab
            isSelected={page === 'fees'}
            to={PATH.reportsSingleTradeFeesRates(source, tradeId)}
            value="fees"
          >
            Fees
          </Tabs.Tab>
          <Tabs.Tab
            isSelected={page === 'history-documents'}
            to={PATH.reportsSingleTradeHistoryAndDocuments(source, tradeId)}
            value="history-documents"
          >
            History and documents
          </Tabs.Tab>
          <Tabs.Tab
            isSelected={page === 'trade-risk'}
            to={PATH.reportsSingleTradeTradeRiskDetails(source, tradeId)}
            value="trade-risk"
          >
            Trade risk
          </Tabs.Tab>
          {isClosable ? (
            <Tabs.Tab
              isSelected={page === 'close'}
              to={PATH.reportsSingleTradeClose(source, tradeId)}
              value="close"
            >
              Close trade
            </Tabs.Tab>
          ) : null}
        </Tabs.List>
        {isEditable || canAmendFees ? (
          <Space isflex alignItems="center" className="ml-auto">
            {canAmendFees ? (
              // A direct flex sibling of the "Edit" link so the shared .root rule gives both the
              // same 16px size, centred alignment and -12px offset. It is an action, not
              // navigation, so it stays a button and opens a confirmation before igniting.
              <LinkButton
                className={classes.amendFees}
                disabled={isAmending}
                onClick={() => setIsConfirmingAmend(true)}
              >
                {isAmending ? 'Starting fee amendment…' : 'Amend fees'}
              </LinkButton>
            ) : null}
            {isEditable ? (
              <Link to={PATH.tradeBuilderEdit(tradeId)} variant="link">
                Edit, top up or withdrawal
              </Link>
            ) : null}
          </Space>
        ) : null}
      </div>
      <Rule />
      {isConfirmingAmend ? (
        <ConfirmationModal
          description={`This starts a forward-only fee amendment${
            tradingAccount?.accountName ? ` for ${tradingAccount.accountName}` : ''
          }. The account is submitted for economics approval, where you can change the portfolio fee (its counterparty or rate) before it is approved. Holdings are not rebalanced and no trades are raised. Do you want to continue?`}
          title="Amend portfolio fees"
          onClose={() => setIsConfirmingAmend(false)}
          onConfirm={() => {
            setIsConfirmingAmend(false)
            amendFees(tradingAccount?.accountId ?? tradeId, currentPortfolioId as string)
          }}
        />
      ) : null}
      <ToastNotification
        description={amendError ?? ''}
        notificationType="error"
        open={!!amendError}
        title="Fee amendment"
        onClose={dismissError}
      />
    </>
  )
}

const InternalTradeTabs = () => {
  const { tradeId } = useParams()
  const { data: tradingAccount, isLoading } =
    useGetByTenantAccountInprogressAndAccountIdentifierQuery({
      tenant: TENANT,
      accountIdentifier: tradeId
    })

  if (isLoading) return null

  return <TradeTabList internal tradingAccount={tradingAccount} />
}

export const TradeTabs = () => {
  const { source } = useParams()

  return source.toLowerCase() === 'internal' ? (
    <InternalTradeTabs />
  ) : (
    <TradeTabList internal={false} />
  )
}
